import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert } from "reactstrap";
import { BusinessRule, BusinessRulesClient, OrderStatus } from "../client/procescontroleclient";
import Button from "../components/UI/Button";
import Input from "../components/UI/Input";
import PageTitle from "../components/UI/PageTitle";
import Select from "../components/UI/InputSelectSearch";
import styled from 'styled-components';
import { ButtonType, InputType } from "../Types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import ButtonWrap from "../components/UI/ButtonWrap";

const ContainerWrap = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;
const Wrapper = styled.div`
    padding: 1.5rem;
    width: 40%;
    margin: 0 auto;
    background: #fff;
    margin-top: 2rem;
    @media(max-width: 1024px) {
        width: 100%;
    }
`;
type FormProps = {
    location?: Location | undefined
}
export default function BusinessRuleForm() {
    const client = new BusinessRulesClient(process.env.REACT_APP_APIURL);
    const location = useLocation();
    const navigate = useNavigate();
    const { businessRuleId } = useParams();
    const queryClient = useQueryClient();
    const isEditing = businessRuleId !== undefined;

    const [businessRule, setBusinessRule] = React.useState<BusinessRule>(new BusinessRule({
        id: 0,
        startDate: moment().toDate(),
        endDate: moment().add(1, 'day').toDate(),
        value: 0,
        orderStatusId: OrderStatus.VoorraadBloemen
    }));
    React.useEffect(() => {
        if (isEditing) {
            setBusinessRule(location.state as BusinessRule);
        }
    }, []);

    const createBusinessRuleMutation = useMutation(async (businessRule: BusinessRule) => {
        return await client.createBusinessRule(businessRule);
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(['businessRules', 'all']);
        }
    });

    const updateBusinessRuleMutation = useMutation(async (businessRule: BusinessRule) => {
        if (businessRule.id) {
            return await client.updateBusinessRule(businessRule.id, businessRule);
        }
        return;
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(['businessRules', 'all']);
        }
    });

    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setBusinessRule({ ...businessRule, [name]: value } as BusinessRule);
    }

    const handleSelectChange = (value: OrderStatus) => {
        setBusinessRule({ ...businessRule, orderStatusId: value } as BusinessRule);
    }

    const orderStatusOptions = [
        { value: OrderStatus.VoorraadBloemen, label: "Voorraad Bloemen" },
        { value: OrderStatus.VoorraadPlanten, label: "Voorraad Planten" },
        { value: OrderStatus.Kubus, label: "Kubus" },
        { value: OrderStatus.Inpak, label: "Inpak" },
        { value: OrderStatus.Expeditie, label: "Expeditie" }
    ]

    return (
        <ContainerWrap>
            <ButtonWrap>
                <Button buttonType={ButtonType.Cancel} svg onClick={() => navigate('/businessrules')}><FontAwesomeIcon icon={faChevronLeft}/> Terug naar overzicht</Button>
            </ButtonWrap>
            <PageTitle>{isEditing ? 'Edit' : 'Create'} Business Rule</PageTitle>
            <Wrapper>
                <Select
                    options={orderStatusOptions}
                    name="orderStatusId"
                    onChange={handleSelectChange}
                    value={businessRule?.orderStatusId}
                    label={"Scherm"} isDisabled={undefined} isAutoFocus={undefined} invalidfield={undefined} nomargin={undefined} zIndexFix={undefined} spacingR={undefined} customFilter={undefined} />
                <Input label="Startdatum" inputType={InputType.LessPadding} type="date" name="startDate" value={moment(businessRule?.startDate).format('YYYY-MM-DD')} onChange={handleFormChange} />
                <Input label="Einddatum" inputType={InputType.LessPadding} type="date" name="endDate" value={moment(businessRule?.endDate).format('YYYY-MM-DD')} onChange={handleFormChange} />
                <div>
                    <Input label="Waarde" inputType={InputType.LessPadding} type="number" name="value" value={businessRule?.value} onChange={handleFormChange} />
                </div>
                {
                    isEditing ? (
                        <Button buttonType={ButtonType.Primary} spacingT onClick={() => updateBusinessRuleMutation.mutate(businessRule)}>Update</Button>
                    ) : (
                        <Button buttonType={ButtonType.Secondary} spacingT onClick={() => createBusinessRuleMutation.mutate(businessRule)}>Create</Button>
                    )
                }
            </Wrapper>
            {
                (updateBusinessRuleMutation.isError || createBusinessRuleMutation.isError) && <Alert color="danger">Er is iets fout gegaan bij het aanmaken van deze business rule. Contacteer de Service Desk.</Alert>
            }
            {
                (updateBusinessRuleMutation.isSuccess || createBusinessRuleMutation.isSuccess) && <Alert color="success">Succesvol opgeslagen</Alert>
            }
        </ContainerWrap>

    )
}